import { batchStoreFetchingHandler, removeSingleNestedStore, fetchErrorNestedStores } from 'helpers/reducers.js';

export function InsightsSectionEntityDefault() {
  return {
    data: {},
    fetched: false,
    fetching: false,
    status: null,
    shouldFetch: true,
    clear: false,
  };
}

export function InsightsListSectionDefault() {
  return {
    data: [],
    fetched: false,
    fetching: false,
    status: null,
    shouldFetch: true,
    clear: false,
    pagination: {
      current: null,
      max: null,
    },
  };
}

/*
  AddInsightsEntity creates an empty entity store object within
  each section of the Insights store (content, publishers, keywords),
  in order to be picked up by StoreSync and filled via a fetch.
  This would be the result of hitting the 'compare' button on an
  Entity to then compare against one or more entities.
*/
export const AddInsightsEntity = (state, action) => {
  const entityId = action.data.public_id;
  const entityType = action.data.type;
  const entityBlankStore = InsightsSectionEntityDefault(entityId);
  const entityBlankListStore = InsightsListSectionDefault(entityId);
  const newEntityStoreObj = { [entityId]: entityBlankStore };
  const newListEntityStoreObj = { [entityId]: entityBlankListStore };
  const newMetadataObj = { [entityId]: action.data };
  const reverseEntityType = entityType === 'brand' ? 'publishers' : 'brands';
  const newState = {
    ...state,
    ...{ content: { ...state.content, ...newEntityStoreObj } },
    ...{ [reverseEntityType]: { ...state[reverseEntityType], ...newListEntityStoreObj } },
    ...{ keywords: { ...state.keywords, ...newListEntityStoreObj } },
    ...{ hubs: { ...state.hubs, ...newListEntityStoreObj } },
    ...{ metadata: { ...state.metadata, ...newMetadataObj } },
  };
  return newState;
};

export const AddPartnershipInsightsEntity = (state, action) => {
  const entityType = action.entityType;
  const entityMetaData = action.data[entityType];
  const entityId = entityMetaData.public_id;
  const entityBlankStore = InsightsSectionEntityDefault(entityId);
  const entityBlankListStore = InsightsListSectionDefault(entityId);
  const newEntityStoreObj = { [entityId]: entityBlankStore };
  const newListEntityStoreObj = { [entityId]: entityBlankListStore };
  const newMetadataObj = { [entityId]: entityMetaData };
  const newState = {
    ...state,
    ...{ content: { ...state.content, ...newEntityStoreObj } },
    ...{ keywords: { ...state.keywords, ...newListEntityStoreObj } },
    ...{ metadata: { ...state.metadata, ...newMetadataObj } },
  };
  return newState;
};

/*
  RemoveInsightsEntity removes a nested entity store by traversing
  each section store (content, publishers & keywords) and matching the key.
  removeSingleNestedStore handles the removal from the nested section state
  then RemoveInsightsEntity buttons everything back up.
*/
export const RemoveInsightsEntity = (state, action) => {
  const entityId = action.entityId;
  const filteredContentStore = removeSingleNestedStore(state.content, entityId);
  const filteredPublishersStore = removeSingleNestedStore(state.publishers, entityId);
  const filteredBrandsStore = removeSingleNestedStore(state.brands, entityId);
  const filteredKeywordsStore = removeSingleNestedStore(state.keywords, entityId);
  const filteredHubsStore = removeSingleNestedStore(state.hubs, entityId);
  const filteredMetadataStore = removeSingleNestedStore(state.metadata, entityId);

  const newState = {
    ...state,
    ...{ content: filteredContentStore },
    ...{ publishers: filteredPublishersStore },
    ...{ brands: filteredBrandsStore },
    ...{ hubs: filteredHubsStore },
    ...{ keywords: filteredKeywordsStore },
    ...{ metadata: filteredMetadataStore },
  };
  return newState;
};

export const navigationInsightsEntity = (state, action, defaultState) => {
  const path = action.payload.location.pathname;
  const pathSplit = path.split('/');
  const currentId = pathSplit[3];
  const isInsightsPage = pathSplit[4] === 'owned_insights' || pathSplit[4] === 'insights';

  let newState = { ...state };

  for (const entityId in state.metadata) {
    if (entityId !== currentId) {
      newState = {
        ...RemoveInsightsEntity(newState, { entityId }),
        filters: defaultState.filters,
      };
    } else if (!isInsightsPage) {
      /*
        If a user navigates out of an Insights page of the same entity
        such as a nested hub or partnership, we need to wipe the insights filters
      */
      newState = { ...newState, filters: defaultState.filters };
    }
  }

  return newState;
};
/*
  InsightsEntityFetchHandler will manage updating the 'fetch' based keys
  (fetched, fetching, shouldFetch) within an entity that is nested in the
  store such as insights.content[entityId], when new data is being fetched.
  This will allow you to wrap a StoreSync around a specific entity, without
  affecting other entities within the same section/tab
*/
export const InsightsEntityFetchHandler = (state, action, section) => {
  const entityId = action.entityId;
  if (!entityId) return { ...state };
  // If entity id exists, update fetching related keys of object
  const existingEntityData = state[section][entityId] || InsightsSectionEntityDefault();
  const update = { fetching: true, fetched: false, shouldFetch: false };

  // Merge existing data with fetching update
  const newEntityData = {
    ...existingEntityData,
    ...update,
  };
  const sectionData = {
    ...state[section],
    ...{ [entityId]: newEntityData },
  };

  return { ...state, ...{ [section]: sectionData } };
};

/*
  InsightsEntitySuccessHandler will be called once a successful fetch has been
  made in the fetch-actions utils and  manage updating the data list within an
  entity that is nested in the store such as insights.content[entityId], when
  new data is being fetched. This will allow you to wrap a StoreSync around a
  specific entity, without affecting other entities within the same section/tab
*/
export const InsightsEntitySuccessHandler = (state, action, section) => {
  const { data = {} } = action;
  const { metadata = {}, public_id } = data;
  const entityId = metadata.public_id || public_id || action.entityId;

  if (!entityId || action.status === 'not found' || action.status === 'error') return fetchErrorNestedStores(state, section);
  // If entity id exists, update, otherwise new data structure
  const existingEntityData = state[section][entityId] || InsightsSectionEntityDefault();
  const newEntityData = {
    ...existingEntityData,
    data: { ...existingEntityData.data, ...action.data },
    status: action.status,
    fetching: false,
    fetched: true,
    shouldFetch: false,
  };

  // Update the section with new entity key
  const sectionData = {
    ...state[section],
    ...{ [entityId]: newEntityData },
  };

  return { ...state, ...{ [section]: sectionData } };
};

// updates the state when the request for new / more content is made
export function InsightsEntityListFetchHandler(state, action, section, clearData = []) {
  const entityId = action.entityId;
  if (!entityId) return { ...state };
  // If entity id exists, update fetching related keys of object
  const existingEntityData = state[section][entityId] || InsightsListSectionDefault();
  const update = { fetching: true, fetched: false, shouldFetch: false };

  // clear out the current data
  if (action.clear) update.data = clearData;

  // Merge existing data with fetching update
  const newEntityData = {
    ...existingEntityData,
    ...update,
  };
  const sectionData = {
    ...state[section],
    ...{ [entityId]: newEntityData },
  };

  return { ...state, ...{ [section]: sectionData } };
}

export const InsightsEntityListSuccessHandler = (state, action, section) => {
  const entityId = action.entityId;

  if (!entityId || action.status === 'not found' || action.status === 'error') return fetchErrorNestedStores(state);

  // If entity id exists, update, otherwise new data structure
  const existingEntityData = state[section][entityId] || InsightsListSectionDefault();
  const newEntityData = {
    data: [...existingEntityData.data, ...action.data],
    pagination: action.pagination || {},
    sortFilters: state[section][entityId].sortFilters,
    status: action.status,
    fetching: false,
    fetched: true,
    shouldFetch: false,
  };

  // Update the section with new entity key
  const sectionData = {
    ...state[section],
    ...{ [entityId]: newEntityData },
  };

  return { ...state, ...{ [section]: sectionData } };
};

/*
  InsightsSetFilterHandler's job is to handle updating the top level filters
  in the insights store, while also running through each section's
  store (content, publisher or keywords) and set each entity store
  for refetching
 */
export const InsightsSetFilterHandler = (state, action) => {
  const { filters } = action;
  let newFilters;
  let clear = false;

  if (Object.keys(filters).length > 0) {
    newFilters = Object.assign({}, state.filters, filters);
    clear = clearFilters(state.filters, newFilters);
  } else {
    newFilters = {};
    clear = true;
  }

  // Go through each entity within the nested sections and batch update fetching keys
  const fetchReadyContent = batchStoreFetchingHandler(state.content, Object.keys(state.content), clear);
  const fetchReadyPublishers = batchStoreFetchingHandler(state.publishers, Object.keys(state.publishers), clear);
  const fetchReadyBrands = batchStoreFetchingHandler(state.brands, Object.keys(state.brands), clear);
  const fetchReadyHubs = batchStoreFetchingHandler(state.hubs, Object.keys(state.hubs), clear);
  const fetchReadyKeywords = batchStoreFetchingHandler(state.keywords, Object.keys(state.keywords), clear);

  // Combine and button it all up
  const newState = {
    ...state,
    ...{ content: { ...state.content, ...fetchReadyContent } },
    ...{ publishers: { ...state.publishers, ...fetchReadyPublishers } },
    ...{ brands: { ...state.brands, ...fetchReadyBrands } },
    ...{ hubs: { ...state.hubs, ...fetchReadyHubs } },
    ...{ keywords: { ...state.keywords, ...fetchReadyKeywords } },
    ...{ filters: newFilters },
  };

  return newState;
};

// clear sortBy, sortOrder, and page of a table filter
function clearFilters(oldFilters, newFilters) {
  let clear = false;
  // sort update
  const sortUpdate = oldFilters.sortOrder !== newFilters.sortOrder || oldFilters.sortBy !== newFilters.sortBy;
  // page update
  const pageReset = newFilters.pageNum === 1;

  if (pageReset || sortUpdate) {
    clear = true;
  }

  return clear;
}
